import {
  Directive,
  EventEmitter,
  HostListener,
  Input,
  Output,
  inject,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { ActivityWithCategoryAndLocationsAndReportedDetails } from '@wilson/interfaces';
import {
  DragAndDropModeService,
  TabEnum,
  TimelineHelperService,
} from '@wilson/shift-timeline/services';
import { SetDemarcatorRange } from '@wilson/shift-timeline/state';
import { firstValueFrom } from 'rxjs';
import { ActivityDropZoneHelperService } from '../../activity-drop-zone/activity-drop-zone-helper.service';

@Directive({
  selector: '[wilsonActivityDragHandler]',
  standalone: true,
})
export class ActivityDragHandlerDirective {
  private readonly store = inject(Store);
  readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly activityDropZoneHelperService = inject(
    ActivityDropZoneHelperService,
  );
  protected readonly timelineHelperService = inject(TimelineHelperService);

  @Input() isActivityDraggable: boolean | null = false;
  @Input() hasServiceUpdatePermissions = false;
  @Input() activity!: ActivityWithCategoryAndLocationsAndReportedDetails;
  @Input() userId!: string | null;
  @Input() leftPx!: number | null;
  @Input() rightPx!: number | null;
  @Output() isTooltipVisible = new EventEmitter();
  @Output() isFiltered = new EventEmitter();

  @HostListener('drag', ['$event']) async onDrop(
    event: DragEvent,
  ): Promise<void> {
    if (this.isActivityDraggable && this.hasServiceUpdatePermissions) {
      this.drag(event);
    }
  }
  @HostListener('dragstart', ['$event']) async onDragStart(
    event: DragEvent,
  ): Promise<void> {
    this.isTooltipVisible.emit(false);

    if (this.hasServiceUpdatePermissions) {
      if (this.isActivityDraggable) {
        this.startDrag(event);
      }
    } else {
      event.stopPropagation();
      event.preventDefault();
      this.timelineHelperService.informUserHasNoPermissionsToMoveEntity();
      setTimeout(() => {
        this.isTooltipVisible.emit(true);
      }, 600);
    }
  }

  @HostListener('dragend', ['$event']) async onDragEnd(
    event: DragEvent,
  ): Promise<void> {
    if (this.isActivityDraggable && this.hasServiceUpdatePermissions) {
      this.dragEnd(event);
    }
  }

  startDrag(event: DragEvent): void {
    this.isFiltered.emit(false);
    this.dragAndDropModeService.setUnassignedRegionInUrl(
      TabEnum.UnassignedActivities,
    );
    setTimeout(async () => {
      const isAssignmentMode = await firstValueFrom(
        this.dragAndDropModeService.isAssignmentMode$,
      );
      const isMoveMode = await firstValueFrom(
        this.dragAndDropModeService.isMoveMode$,
      );
      if (isAssignmentMode) {
        this.dragVertically(this.activity, this.leftPx, this.rightPx);
      }
      if (isMoveMode) {
        this.dragHorizontally(this.activity);
      }
    });
    event.stopPropagation();
  }

  drag(event: DragEvent): void {
    event.stopPropagation();
  }

  dragEnd(event: DragEvent): void {
    event.stopPropagation();
    this.isTooltipVisible.emit(true);
    this.isFiltered.emit(true);
    this.store.dispatch(new SetDemarcatorRange([]));
    this.dragAndDropModeService.setActivityDropZone(false);
    this.dragAndDropModeService.setMoveActivityDropZone(false);
    this.dragAndDropModeService.setDraggingItemId(null);
    this.activityDropZoneHelperService.setUserId(null);
  }

  dragVertically(
    activity: ActivityWithCategoryAndLocationsAndReportedDetails,
    leftPx: number | null,
    rightPx: number | null,
  ): void {
    this.activityDropZoneHelperService.setActivity(activity);
    this.activityDropZoneHelperService.setUserId(this.userId);
    if (leftPx !== null && rightPx !== null) {
      this.store.dispatch([
        new SetDemarcatorRange([
          {
            left: leftPx,
            right: rightPx,
          },
        ]),
      ]);
      this.dragAndDropModeService.setActivityDropZone(true);
      if (activity.id) {
        this.dragAndDropModeService.setDraggingItemId(activity.id);
      }
    }
  }

  dragHorizontally(
    activity: ActivityWithCategoryAndLocationsAndReportedDetails,
  ): void {
    if (activity.id) {
      this.activityDropZoneHelperService.setActivity(activity);
      this.activityDropZoneHelperService.setUserId(this.userId);
      this.dragAndDropModeService.setMoveActivityDropZone(true);
      this.dragAndDropModeService.setDraggingItemId(activity.id);
    } else {
      console.error('Error: Dragging failed - Activity must have an ID.');
    }
  }
}
